import type {
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { Effect } from "effect";

/**
 * Centralized voice RPC handlers for the support-v2 voice widget. All
 * effect-api calls live here as named hooks — components and the v2
 * VoiceContextV2 provider consume these instead of importing
 * `effectApi.runPromise` directly. Keeps RPC boilerplate in one file and
 * gives us one place to layer in cache-invalidation, telemetry, or retry.
 *
 * Pattern mirrors `support-v2/actions/use-support-ticket-actions.ts`.
 */

type VoiceAvailability = "available" | "in-call" | "unavailable";

export function useGetTelnyxTelephonyToken() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: { targetMongoUserId?: MongoUserId }): Promise<string> => {
      const { token } = await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.getTelnyxTelephonyToken({ payload: input })
          .pipe(Effect.orDie),
      );
      return token;
    },
  );
}

export function useSetVoiceAvailability() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      status: VoiceAvailability;
      targetMongoUserId?: MongoUserId;
    }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.setVoiceAvailability({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

export interface PrepareOutboundCallResult {
  readonly messageId: MessageId;
  readonly clientState: string;
  readonly fromPhoneNumber: string;
  readonly toPhoneNumber: string;
}

export function usePrepareOutboundCall() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      customerPhoneNumber: string;
    }): Promise<PrepareOutboundCallResult> =>
      effectApi.runPromise(
        effectApi
          .getClient()
          .voice.prepareOutboundCall({ payload: input })
          .pipe(Effect.orDie),
      ),
  );
}

export function useSetCallHold() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: { messageId: MessageId; onHold: boolean }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.setCallHold({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

export function useSetCallRecording() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      messageId: MessageId;
      recording: boolean;
    }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.setCallRecording({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

export interface LookedUpCustomer {
  readonly mongoCustomerId: string;
  readonly name: string;
  readonly imageUrl: string | null;
}

export function useLookupCustomerByPhone() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      phoneNumber: string;
    }): Promise<LookedUpCustomer | null> => {
      const { customer } = await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.lookupCustomerByPhone({ payload: input })
          .pipe(Effect.orDie),
      );
      return customer
        ? {
            mongoCustomerId: String(customer.mongoCustomerId),
            name: customer.name,
            imageUrl: customer.imageUrl,
          }
        : null;
    },
  );
}

export function useGetVoiceQueue() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(async () => {
    const { entries } = await effectApi.runPromise(
      effectApi.getClient().voice.getVoiceQueue().pipe(Effect.orDie),
    );
    return entries;
  });
}

export function useGetVoiceCallAvailability() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(async () => {
    const { agents } = await effectApi.runPromise(
      effectApi.getClient().voice.getVoiceCallAvailability().pipe(Effect.orDie),
    );
    return agents;
  });
}

export function useGetActiveRings() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(async () => {
    const { rings } = await effectApi.runPromise(
      effectApi.getClient().voice.getActiveRings().pipe(Effect.orDie),
    );
    return rings;
  });
}

export function useGetRecentCalls() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(async () => {
    const { calls } = await effectApi.runPromise(
      effectApi.getClient().voice.getRecentCalls().pipe(Effect.orDie),
    );
    return calls;
  });
}

export function useAddAgentToCall() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      messageId: MessageId;
      targetMongoUserId: MongoUserId;
    }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.addAgentToCall({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

export function useAddPhoneNumberToCall() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      messageId: MessageId;
      phoneNumber: string;
    }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.addPhoneNumberToCall({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

/**
 * One participant currently in a call's Telnyx conference. The active-
 * call section renders these as avatar+name rows so the agent sees
 * exactly who is on the line as warm-transfer legs join/leave.
 */
export type ConferenceParticipant =
  | {
      readonly kind: "customer";
      readonly callControlId: string;
      readonly name: string;
      readonly phoneNumber: string;
    }
  | {
      readonly kind: "agent";
      readonly callControlId: string;
      readonly mongoUserId: MongoUserId;
      readonly name: string;
    }
  | {
      readonly kind: "external";
      readonly callControlId: string;
      readonly phoneNumber: string;
    }
  | {
      readonly kind: "forwarded_phone";
      readonly callControlId: string;
      readonly mongoUserId: MongoUserId;
      readonly phoneNumber: string;
    };

export function useGetConferenceParticipants() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      messageId: MessageId;
    }): Promise<{
      readonly participants: readonly ConferenceParticipant[];
      readonly supportTicketId: SupportTicketId | null;
    }> => {
      const { participants, supportTicketId } = await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.getConferenceParticipants({ payload: input })
          .pipe(Effect.orDie),
      );
      return { participants, supportTicketId };
    },
  );
}

/**
 * Historical participant view used by the dev test harness — includes
 * legs that have already left, with their join/leave timestamps. Works
 * for completed calls (the live conference is gone but the rows remain).
 */
export type CallParticipantHistory =
  | {
      readonly kind: "customer";
      readonly callControlId: string;
      readonly name: string;
      readonly phoneNumber: string;
    }
  | {
      readonly kind: "agent";
      readonly callControlId: string | null;
      readonly mongoUserId: MongoUserId;
      readonly name: string;
      readonly rangAt: Date | null;
      readonly answeredAt: Date | null;
      readonly leftAt: Date | null;
    }
  | {
      readonly kind: "external";
      readonly callControlId: string | null;
      readonly phoneNumber: string;
      readonly rangAt: Date | null;
      readonly answeredAt: Date | null;
      readonly leftAt: Date | null;
    };

export function useGetMyVoiceForwardingNumber() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(async (): Promise<string | null> => {
    const { forwardingPhoneNumber } = await effectApi.runPromise(
      effectApi
        .getClient()
        .voice.getMyVoiceForwardingNumber()
        .pipe(Effect.orDie),
    );
    return forwardingPhoneNumber;
  });
}

export function useSetMyVoiceForwardingNumber() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: { forwardingPhoneNumber: string | null }): Promise<void> => {
      await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.setMyVoiceForwardingNumber({ payload: input })
          .pipe(Effect.orDie),
      );
    },
  );
}

export function useGetCallParticipants() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  return useHandler(
    async (input: {
      messageId: MessageId;
    }): Promise<readonly CallParticipantHistory[]> => {
      const { participants } = await effectApi.runPromise(
        effectApi
          .getClient()
          .voice.getCallParticipants({ payload: input })
          .pipe(Effect.orDie),
      );
      return participants;
    },
  );
}
